import axios from "axios";
import {BaseUserResponse,  RegisterPayload, RegisterResponse,} from "../types/models/"

const REACT_APP_API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

export const createUser = async (
  userType: string,
  data: RegisterPayload,
  isSecure: boolean = false
): Promise<RegisterResponse> => {
  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (isSecure) {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Unauthorized: No token found.");
      }
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await axios.post(`${REACT_APP_API_URL}/${userType}/register`, data, {
      headers,
    });

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Registration Error:", error.response?.data || error.message);
      throw new Error(error.response?.data?.detail || error.message);
    } else {
      console.error("Generic Registration Error:", (error as Error).message);
      throw error;
    }
  }
};
export const getUser = async <R>(userType: string, userId: number): Promise<R> => {
  const token = localStorage.getItem("authToken");

  if (!token) {
    throw new Error("Unauthorized: No token found.");
  }

  try {
    const response = await axios.get(`${REACT_APP_API_URL}/${userType}/${userId}`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Error fetching user:", error.response?.data || error.message);
      throw new Error(error.response?.data?.detail || error.message);
    } else {
      console.error("Generic Error:", (error as Error).message);
      throw error;
    }
  }
};

export const updateUser = async <T extends object, R>(
  userType: string,
  userId: number,
  data: T
): Promise<R> => {
  const token = localStorage.getItem("authToken");

  if (!token) {
    throw new Error("Unauthorized: No token found.");
  }

  const response = await fetch(`${REACT_APP_API_URL}/${userType}/${userId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to update user");
  }

  return response.json();
};

export const deleteUser = async (
  userType: string,
  userId: number,
  token: string
): Promise<boolean> => {
  const response = await fetch(`${REACT_APP_API_URL}/${userType}/${userId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to delete user");
  }

  return response.json();
};

export const getAllUsers = async <T extends BaseUserResponse>(userType: string): Promise<T[]> => {
  const token = localStorage.getItem("authToken");
  if (!token) throw new Error("Unauthorized: No token found.");

  const response = await axios.get(`${REACT_APP_API_URL}/${userType}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const getDetailedUserInfo = async <R>(userType: string): Promise<R> => {
  const token = localStorage.getItem("authToken");

  if (!token) {
    throw new Error("Unauthorized: No token found.");
  }

  try {
    const response = await axios.get(`${REACT_APP_API_URL}/${userType}/info`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error(`Error fetching detailed ${userType} info:`, error);
    throw new Error(`Failed to fetch detailed ${userType} info.`);
  }
};
